import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIdentity } from 'pages/Identity/Login'
import { fetchJobsFromUrl } from '../../../redux/aiJobs'
import posthog from 'posthog-js'
import { fetchCreateBulkJobs } from '../../../redux/jobs'
import {
  Drawer,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  IconButton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import URLInputStep from './URLInputStep'
import JobReviewStep from './JobReviewStep'

const steps = ['Add URLs', 'Review Jobs', 'Upload Complete']

const SmartUploadDrawer = ({ open, onClose }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { loading: reduxLoading } = useSelector(state => state.aiJobs)
  const { isLoadingJobs: bulkJobsLoading } = useSelector(state => state.jobs)
  const { employerId, employerUserId } = useIdentity()
  
  const [activeStep, setActiveStep] = useState(0)
  const [urls, setUrls] = useState([''])
  const [extractedJobs, setExtractedJobs] = useState([])
  const [selectedJobs, setSelectedJobs] = useState([])
  const [uploadedJobsCount, setUploadedJobsCount] = useState(0)

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  const handleStepClick = (stepIndex) => {
    // Only allow going back to previous steps, not forward
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex)
    }
  }

  const handleClose = () => {
    posthog.capture('smart_upload_drawer_closed', { step: activeStep })
    setActiveStep(0)
    setUrls([''])
    setExtractedJobs([])
    setSelectedJobs([])
    setUploadedJobsCount(0)
    onClose()
  }

  const handleUrlsSubmit = async (submittedUrls) => {
    setUrls(submittedUrls)
    posthog.capture('smart_upload_urls_submitted', { url_count: submittedUrls.filter(u => u.trim()).length })

    try {
      // Use Redux action to fetch jobs from URLs
      const jobs = await dispatch(fetchJobsFromUrl(submittedUrls))
      
      setExtractedJobs(jobs)
      // Initialize all jobs as selected (component decides selection logic)
      setSelectedJobs(jobs)
      handleNext()
    } catch (error) {
      console.error('Error parsing URLs:', error)
    }
  }

  const handleJobUpdate = (updatedJob) => {
    setExtractedJobs(prev => 
      prev.map(job => job.id === updatedJob.id ? updatedJob : job)
    )
  }

  const handleJobSelection = (jobId, isSelected) => {
    const job = extractedJobs.find(j => j.id === jobId)
    if (job) {
      const updatedJob = { ...job, isSelected }
      handleJobUpdate(updatedJob)
      
      if (isSelected) {
        setSelectedJobs(prev => [...prev.filter(j => j.id !== jobId), updatedJob])
      } else {
        setSelectedJobs(prev => prev.filter(j => j.id !== jobId))
      }
    }
  }

  const handleBulkSelection = (isSelected) => {
    const updatedJobs = extractedJobs.map(job => ({ ...job, isSelected }))
    setExtractedJobs(updatedJobs)
    setSelectedJobs(isSelected ? updatedJobs : [])
  }

  const handleUpload = async () => {
    try {
      await dispatch(fetchCreateBulkJobs({
        jobs: selectedJobs,
        urls: urls,
        employerId: employerId,
        employerUserId: employerUserId
      }))

      posthog.capture('smart_upload_completed', { job_count: selectedJobs.length })
      setUploadedJobsCount(selectedJobs.length)
      handleNext()
    } catch (error) {
      console.error('Error uploading jobs:', error)
    }
  }

  const handleViewJobs = () => {
    // Close drawer and navigate to jobs page with indication of new uploads
    handleClose()
    navigate('/jobs', { state: { newlyUploaded: uploadedJobsCount } })
  }

  const handleCloseDrawer = () => {
    // Simple close without navigation
    handleClose()
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <URLInputStep
            urls={urls}
            onUrlsChange={setUrls}
            onSubmit={handleUrlsSubmit}
            onCancel={handleClose}
            isLoading={reduxLoading}
          />
        )
      case 1:
        return (
          <JobReviewStep
            jobs={extractedJobs}
            selectedJobs={selectedJobs}
            onJobUpdate={handleJobUpdate}
            onJobSelection={handleJobSelection}
            onBulkSelection={handleBulkSelection}
            onBack={handleBack}
            onUpload={handleUpload}
            isLoading={bulkJobsLoading}
          />
        )
      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" sx={{ color: 'success.main', mb: 2 }}>
                ✅ Upload Complete!
              </Typography>
              <Typography variant="h6" gutterBottom>
                Successfully uploaded {uploadedJobsCount} job{uploadedJobsCount !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your jobs have been added and are now live on your jobs page.
              </Typography>
            </Box>
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', sm: 600, md: 800 },
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ width: 40 }} /> {/* Spacer for centering */}
          <Typography variant="h6" component="h2">
            Smart Job Upload
          </Typography>
          <IconButton
            onClick={handleCloseDrawer}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Paper elevation={0} sx={{ p: 3, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel 
                  sx={{
                    cursor: index < activeStep ? 'pointer' : 'default',
                    '& .MuiStepLabel-label': {
                      color: index < activeStep ? 'primary.main' : 'inherit',
                      '&:hover': index < activeStep ? { 
                        textDecoration: 'underline' 
                      } : {}
                    }
                  }}
                  onClick={() => handleStepClick(index)}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {renderStepContent()}
        </Box>

        {/* Fixed Bottom Buttons for Success Step */}
        {activeStep === 2 && (
          <Box sx={{ 
            p: 3, 
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2
          }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)} // Go back to URLs
              size="large"
            >
              Upload More Jobs
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCloseDrawer}
                size="large"
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleViewJobs}
                size="large"
              >
                View Jobs
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

export default SmartUploadDrawer